import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import type {
	Config,
	ConfigCreateRequest,
	ProblemDetails,
} from "~/shared/schemas/config-schema.ts";
import {
	configCreateResponseSchema,
	configDeleteSchema,
	configListSchema,
} from "~/shared/schemas/config-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { apiClients } from "~/shared/services/api/fetch-client.ts";
import { getDeploymentType } from "~/shared/utils/get-deployment-type.ts";

import { useNotificationActions } from "./use-notifications.ts";
import { useSharedListsAccess } from "./use-shared-lists-access.ts";

export type UseUserConfigurationReturn = {
	createUserConfig: (body: ConfigCreateRequest) => Promise<void>;
	updateUserConfig: (body: Config) => Promise<void>;
	deleteUserConfig: (id: number) => Promise<void>;
	isCreating: boolean;
	isUpdating: boolean;
	isDeleting: boolean;
	userConfigs: Config[];
	isLoadingConfigs: boolean;
};

export const useUserConfiguration = (): UseUserConfigurationReturn => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();
	const { canManageSharedLists, isLoading: isLoadingAccess } =
		useSharedListsAccess();

	const isExternal = getDeploymentType() === "external";
	const configRoute = isExternal ? apiRoutes.CUSTOMER_CONFIG : apiRoutes.CONFIG;
	const { getAndValidate, postAndValidate, putAndValidate, deleteAndValidate } =
		isExternal ? apiClients.default : apiClients.traxAr;

	const queryIdentifier = isExternal ? ("external" as const) : "internal";

	const { data: userConfigs, isLoading: isLoadingConfigs } = useQuery({
		queryKey: ["userConfigs", queryIdentifier],
		queryFn: async () => {
			const result = await getAndValidate(configRoute, configListSchema);

			if (!result.success) {
				throw new Error(result.error);
			}

			const data = result.data;

			if (Array.isArray(data)) {
				return data;
			}

			if (typeof data === "object" && "title" in data) {
				throw new Error(
					(data as ProblemDetails).detail ||
						"An error occurred while fetching the configurations.",
				);
			}

			return [];
		},
		enabled: canManageSharedLists,
	});

	const createMutation = useMutation({
		mutationFn: async (body: ConfigCreateRequest) => {
			const result = await postAndValidate(
				configRoute,
				body,
				configCreateResponseSchema,
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			// Check if the response is a ProblemDetails error
			if ("title" in result.data) {
				throw new Error(
					result.data.detail ||
						"An error occurred while creating the configuration.",
				);
			}

			return result;
		},
		onMutate: async (newConfig) => {
			await queryClient.cancelQueries({
				queryKey: ["userConfigs", queryIdentifier],
			});
			const previousConfigs = queryClient.getQueryData<Config[]>([
				"userConfigs",
				queryIdentifier,
			]);
			const randomArray = new Uint32Array(1);
			globalThis.crypto.getRandomValues(randomArray);
			const randomId = randomArray[0] ?? 0;
			queryClient.setQueryData<Config[]>(
				["userConfigs", queryIdentifier],
				(old) => [
					...(old ?? []),
					{
						...newConfig,
						id: -(randomId & 0x7f_ff_ff_ff),
					} as Config,
				],
			);
			return { previousConfigs };
		},
		onSuccess: () => {
			showSuccess("User configuration saved successfully.");
		},
		onError: (error: Error, _newConfig, context) => {
			console.error(error);
			if (context?.previousConfigs) {
				queryClient.setQueryData(
					["userConfigs", queryIdentifier],
					context.previousConfigs,
				);
			}
			showError("Failed to create user configuration. Please try again later.");
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: ["userConfigs", queryIdentifier],
			});
		},
	});

	const updateMutation = useMutation({
		mutationFn: async (body: Config) => {
			const result = await putAndValidate(
				`${configRoute}/${body.id}`,
				body,
				configCreateResponseSchema,
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			// Check if the response is a ProblemDetails error
			if ("title" in result.data) {
				throw new Error(
					result.data.detail ||
						"An error occurred while updating the configuration.",
				);
			}

			return result;
		},
		onMutate: async (updatedConfig) => {
			await queryClient.cancelQueries({
				queryKey: ["userConfigs", queryIdentifier],
			});
			const previousConfigs = queryClient.getQueryData<Config[]>([
				"userConfigs",
				queryIdentifier,
			]);
			queryClient.setQueryData<Config[]>(
				["userConfigs", queryIdentifier],
				(old) =>
					old?.map((config) =>
						config.id === updatedConfig.id ? updatedConfig : config,
					),
			);
			return { previousConfigs };
		},
		onSuccess: () => {
			showSuccess("User configuration updated successfully.");
		},
		onError: (error: Error, _updatedConfig, context) => {
			console.error(error);
			if (context?.previousConfigs) {
				queryClient.setQueryData(
					["userConfigs", queryIdentifier],
					context.previousConfigs,
				);
			}
			showError("Failed to update user configuration. Please try again later.");
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: ["userConfigs", queryIdentifier],
			});
		},
	});

	const deleteMutation = useMutation({
		mutationFn: async (id: number) => {
			const result = await deleteAndValidate(
				`${configRoute}/${id}`,
				configDeleteSchema,
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			// If result.data is an object with a title, it's a ProblemDetails error
			if (
				result.data &&
				typeof result.data === "object" &&
				"title" in result.data
			) {
				throw new Error(
					(result.data as ProblemDetails).detail ||
						"An error occurred while deleting the configuration.",
				);
			}

			return result;
		},
		onMutate: async (id) => {
			await queryClient.cancelQueries({
				queryKey: ["userConfigs", queryIdentifier],
			});
			const previousConfigs = queryClient.getQueryData<Config[]>([
				"userConfigs",
				queryIdentifier,
			]);
			queryClient.setQueryData<Config[]>(
				["userConfigs", queryIdentifier],
				(old) => old?.filter((config) => config.id !== id),
			);
			return { previousConfigs };
		},
		onSuccess: () => {
			showSuccess("User configuration removed successfully.");
		},
		onError: (error: Error, _id, context) => {
			console.error(error);
			if (context?.previousConfigs) {
				queryClient.setQueryData(
					["userConfigs", queryIdentifier],
					context.previousConfigs,
				);
			}
			showError("Failed to remove user configuration. Please try again later.");
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: ["userConfigs", queryIdentifier],
			});
		},
	});

	const { mutateAsync: createMutateAsync } = createMutation;
	const { mutateAsync: updateMutateAsync } = updateMutation;
	const { mutateAsync: deleteMutateAsync } = deleteMutation;

	const createUserConfig = useCallback(
		async (body: ConfigCreateRequest): Promise<void> => {
			await createMutateAsync(body);
		},
		[createMutateAsync],
	);

	const updateUserConfig = useCallback(
		async (body: Config): Promise<void> => {
			await updateMutateAsync(body);
		},
		[updateMutateAsync],
	);

	const deleteUserConfig = useCallback(
		async (id: number): Promise<void> => {
			await deleteMutateAsync(id);
		},
		[deleteMutateAsync],
	);

	const memoizedUserConfigs = useMemo(() => userConfigs ?? [], [userConfigs]);

	return {
		createUserConfig,
		updateUserConfig,
		deleteUserConfig,
		isCreating: createMutation.isPending,
		isUpdating: updateMutation.isPending,
		isDeleting: deleteMutation.isPending,
		userConfigs: memoizedUserConfigs,
		isLoadingConfigs: isLoadingAccess || isLoadingConfigs,
	};
};
